import { useKeycloak } from "@react-keycloak/web";
import { useState, useEffect, useMemo, useContext } from "react";
import { ProfileContext } from "../providers/profile-provider";

/**
 * Extends KeycloakTokenParsed to add real world attributes
 */
interface TokenParsed extends Keycloak.KeycloakTokenParsed {
  preferred_username?: string;
  name?: string;
  given_name?: string;
}

const fallBackUsername = (tokenParsed: TokenParsed | undefined) => {
  if (tokenParsed) {
    // `tokenParsed.preferred_username` is from Github
    // most users are sign-up from Github, prefer Github username
    // `tokenParsed.name` seems to be the same as `tokenParsed.given_name`
    const username = tokenParsed.preferred_username ?? tokenParsed.name ?? tokenParsed.given_name ?? "";

    return username;
  }

  return "";
};

/**
 * Get username with fallbacks
 *
 * p0 uses `keycloak.loadprofile`, `keycloak.loadprofile` is needed because access token
 * doesn't contain keycloak username by default.
 * p1 uses `values in keycloak.parseToken` (access token)
 * fallback "" (empty string)
 *
 */
export const useUsername = () => {
  const { keycloak } = useKeycloak();
  const { profile } = useContext(ProfileContext);

  const usernameFromToken = useMemo(() => fallBackUsername(keycloak.tokenParsed), [keycloak.tokenParsed]);

  const [username, setUsername] = useState<String>("");

  useEffect(() => {
    setUsername(usernameFromToken);
  }, [usernameFromToken, keycloak]);

  return profile?.username ? profile?.username : username;
};
